import { cn } from '@/lib/utils';
import { FC, ReactNode, useCallback } from 'react';
import { List, Orderable } from '../../../modifiers/Orderable';
import { InputProps, withField } from '../../withField';
import { IconConfiguration, TextAndIconInput, TextAndIconValue } from '../TextAndIconInput';

interface MultiTextAndIconInputProps {
  placeholder?: string;
  placeholderIcon?: ReactNode;
  icons: IconConfiguration;
}

export const MultiTextAndIconInput: FC<InputProps<TextAndIconValue[]> & MultiTextAndIconInputProps> = ({
  onChange,
  value = [],
  id,
  icons,
  placeholder,
  placeholderIcon
}) => {
  const onReorder = useCallback(
    (source: number, destination: number) => {
      const array = [...value];
      const [removed] = array.splice(source, 1);
      array.splice(destination, 0, removed);
      onChange(array);
    },
    [onChange, value]
  );

  const onDelete = useCallback(
    (id: string) => {
      const array = value.filter(item => `${item.iconId}+${item.text}` !== id);
      onChange(array);
    },
    [onChange, value]
  );

  const onChangeItem = useCallback(
    (index: number) => (input: TextAndIconValue) => {
      const array = [...value];
      array[index] = input;
      onChange(array);
    },
    [onChange, value]
  );

  const onAdd = useCallback(
    (input: TextAndIconValue) => {
      const array = [...value];
      array.push(input);
      onChange(array);
    },
    [onChange, value]
  );

  return (
    <>
      <List
        id="droppable"
        itemIds={value.map(item => `${item.iconId}+${item.text}`)}
        className={cn(value.length > 0 && `mb-2 -mt-2`)}
        onReorder={onReorder}
        onDelete={onDelete}
      >
        {value.map((item, index) => (
          <Orderable
            className="mt-2"
            key={`multiTextAndIconInput-${index}`}
            id={`${item.iconId}+${item.text}`}
            index={index}
          >
            <TextAndIconInput
              icons={icons}
              id={`${item.iconId}+${item.text}`}
              value={item}
              onChange={onChangeItem(index)}
              autofocus={index === value.length - 1}
            />
          </Orderable>
        ))}
      </List>
      <div className="opacity-75">
        <TextAndIconInput
          id={`${id}-add`}
          placeholder={placeholder}
          placeholderIcon={placeholderIcon}
          icons={icons}
          onChange={onAdd}
          value={{ text: '', iconId: '' }}
        />
      </div>
    </>
  );
};

export const MultiTextAndIconField = withField<TextAndIconValue[], MultiTextAndIconInputProps>(MultiTextAndIconInput);
